import { useLayoutEffect, type DependencyList, type RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const MOTION_OK = '(prefers-reduced-motion: no-preference)';

type Preset = { from: gsap.TweenVars; to: gsap.TweenVars; stagger?: number };

const P3 = 'power3.out';

/** Motion vocabulary. Initial states are only applied after JS init succeeds. */
const PRESETS: Record<string, Preset> = {
  title: { from: { opacity: 0, y: 24 }, to: { duration: 0.75, ease: P3 } },
  card: { from: { opacity: 0, y: 32 }, to: { duration: 0.8, ease: P3 } },
  portrait: { from: { opacity: 0, y: 24 }, to: { duration: 0.8, ease: P3 } },
  text: { from: { opacity: 0, y: 18 }, to: { duration: 0.7, ease: P3 } },
  sub: { from: { opacity: 0, y: 14 }, to: { duration: 0.65, ease: P3 } },
  caption: { from: { opacity: 0, y: 12 }, to: { duration: 0.55, ease: P3 }, stagger: 0.06 },
  btn: { from: { opacity: 0, y: 14 }, to: { duration: 0.55, ease: P3 } },
  left: { from: { opacity: 0, x: -22 }, to: { duration: 0.7, ease: P3 } },
  'left-lg': { from: { opacity: 0, x: -32 }, to: { duration: 0.85, ease: P3 } },
  line: { from: { scaleX: 0, transformOrigin: 'left center' }, to: { duration: 0.75, ease: 'power2.inOut' } },
  row: { from: { opacity: 0, y: 16 }, to: { duration: 0.55, ease: P3 }, stagger: 0.055 },
  fade: { from: { opacity: 0 }, to: { duration: 0.35, ease: 'power1.out' } },
  scale: { from: { scale: 0.97 }, to: { duration: 0.9, ease: P3 } },
  focus: { from: { filter: 'blur(4px)', opacity: 0.94, scale: 1.025 }, to: { duration: 0.85, ease: 'power2.out' } },
  clip: { from: { clipPath: 'inset(0% 8% 0% 0%)' }, to: { duration: 1, ease: P3 } },
  zoom: { from: { scale: 1.025 }, to: { duration: 1, ease: P3 } },
  foot: { from: { opacity: 0, y: 16 }, to: { duration: 0.65, ease: P3 } },
};

function finalOf(from: gsap.TweenVars): gsap.TweenVars {
  const out: gsap.TweenVars = {};
  Object.keys(from).forEach((k) => {
    if (k === 'transformOrigin') return;
    if (k === 'opacity' || k === 'scale' || k === 'scaleX') out[k] = 1;
    else if (k === 'filter') out[k] = 'blur(0px)';
    else if (k === 'clipPath') out[k] = 'inset(0% 0% 0% 0%)';
    else out[k] = 0;
  });
  return out;
}

const CLEAR = 'transform,opacity,filter,clipPath,willChange';

/**
 * Group-based choreography.
 * - `[data-group]` = one composition that plays once when it enters (~85% viewport).
 * - `[data-m="preset"]` inside a group, optional `data-at` (timeline seconds).
 * - `[data-m="words"]` splits on child `[data-w]` (Word Cascade).
 * - `[data-m="dir"]` animates `[data-dir-l]` / `[data-dir-r]` (Directional heading).
 * Groups entering together are staggered in document order.
 * Uses IntersectionObserver (real viewport), so pinned Hero spacing cannot skew triggers.
 */
export function useMotion(ref: RefObject<HTMLElement | null>, deps: DependencyList = []) {
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const mm = gsap.matchMedia(root);
    mm.add(MOTION_OK, () => {
      const groups = Array.from(root.querySelectorAll<HTMLElement>('[data-group]'));
      if (root.hasAttribute('data-group')) groups.unshift(root);
      if (!groups.length) return undefined;
      const narrow = window.innerWidth < 768;
      const dirX = narrow ? 14 : 28;
      const mountedAt = performance.now();

      const targetsOf = (g: HTMLElement) => {
        const list = Array.from(g.querySelectorAll<HTMLElement>('[data-m]'))
          .filter((el) => el.closest('[data-group]') === g);
        if (g.hasAttribute('data-m')) list.unshift(g);
        return list;
      };

      // Apply initial states (only now that JS is running).
      groups.forEach((g) => targetsOf(g).forEach((el) => {
        const kind = el.dataset.m ?? '';
        if (kind === 'words') {
          gsap.set(el.querySelectorAll('[data-w]'), { opacity: 0, y: 28, rotateX: 5, transformPerspective: 600, transformOrigin: '50% 100%' });
        } else if (kind === 'dir') {
          gsap.set(el.querySelector('[data-dir-l]'), { opacity: 0, x: -dirX, filter: 'blur(2px)' });
          gsap.set(el.querySelector('[data-dir-r]'), { opacity: 0, x: dirX, filter: 'blur(2px)' });
        } else if (PRESETS[kind]) {
          gsap.set(el, PRESETS[kind].from);
        }
      }));

      const play = (g: HTMLElement, delay: number) => {
        const tl = gsap.timeline({ delay });
        const counts: Record<string, number> = {};
        targetsOf(g).forEach((el) => {
          const kind = el.dataset.m ?? '';
          const at = parseFloat(el.dataset.at ?? '0') || 0;
          if (kind === 'words') {
            tl.to(el.querySelectorAll('[data-w]'), {
              opacity: 1, y: 0, rotateX: 0, duration: 0.8, ease: P3, stagger: 0.09, clearProps: CLEAR,
            }, at);
          } else if (kind === 'dir') {
            tl.to([el.querySelector('[data-dir-l]'), el.querySelector('[data-dir-r]')], {
              opacity: 1, x: 0, filter: 'blur(0px)', duration: 0.85, ease: P3, clearProps: CLEAR,
            }, at);
          } else if (PRESETS[kind]) {
            const p = PRESETS[kind];
            const idx = counts[kind] ?? 0;
            counts[kind] = idx + 1;
            tl.to(el, { ...finalOf(p.from), ...p.to, clearProps: CLEAR }, at + idx * (p.stagger ?? 0));
          }
        });
      };

      const io = new IntersectionObserver((entries) => {
        const entering = entries.filter((e) => e.isIntersecting).map((e) => e.target as HTMLElement);
        if (!entering.length) return;
        entering.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
        // First-paint batch: let the page title lead before content follows.
        const base = performance.now() - mountedAt < 400 ? 0.05 : 0;
        entering.forEach((g, i) => {
          io.unobserve(g);
          const lead = base && i > 0 ? 0.2 : 0;
          play(g, base + lead + Math.min(i, 5) * 0.11);
        });
      }, { rootMargin: '0px 0px -15% 0px', threshold: 0 });

      groups.forEach((g) => io.observe(g));
      return () => io.disconnect();
    });
    return () => mm.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** Selected Work: each `[data-expand] img` scales 0.965 → 1 with a 1.5% inset, scrubbed by its own position. */
export function useScrollExpand(ref: RefObject<HTMLElement | null>, deps: DependencyList = []) {
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const mm = gsap.matchMedia(root);
    mm.add(MOTION_OK, () => {
      root.querySelectorAll<HTMLElement>('[data-expand]').forEach((frame) => {
        const img = frame.querySelector('img');
        if (!img) return;
        gsap.fromTo(
          img,
          { scale: 0.965, clipPath: 'inset(1.5% 1.5% 1.5% 1.5%)' },
          {
            scale: 1,
            clipPath: 'inset(0% 0% 0% 0%)',
            ease: 'none',
            scrollTrigger: { trigger: frame, start: 'top 90%', end: 'top 45%', scrub: 0.8 },
          },
        );
      });
    });
    return () => mm.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** Studio statement: words shift from muted grey to ink, scrubbed by scroll (colour only). */
export function useWordEmphasis(ref: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const mm = gsap.matchMedia(root);
    mm.add(MOTION_OK, () => {
      const block = root.querySelector<HTMLElement>('[data-emphasis]');
      const words = block?.querySelectorAll<HTMLElement>('[data-word]');
      if (!block || !words?.length) return;
      gsap.fromTo(words, { color: '#A7A1AC' }, {
        color: '#171717',
        ease: 'none',
        stagger: 0.5,
        scrollTrigger: { trigger: block, start: 'top 85%', end: 'top 40%', scrub: 0.6 },
      });
    });
    return () => mm.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}